import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleSignInButton from '../components/GoogleSignInButton';
import './Auth.css';

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  });
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [signupError, setSignupError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => {
    setSignupError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignupError('');

    if (formData.password !== formData.confirmPassword) {
      setSignupError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 8) {
      setSignupError('Password must be at least 8 characters.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSignupError(data.error || data.errors?.[0]?.msg || 'Signup failed');
        return;
      }

      setSignupSuccess(true);
      setVerificationEmail(data.email);
    } catch (error) {
      console.error('Signup error:', error);
      setSignupError('Network error. Please try again.');
    }
  };

  if (signupSuccess) {
    return (
      <Box className="auth-page-container ch-page-enter">
        <Fade in timeout={220}>
          <Paper className="auth-page-box ch-card-enter" elevation={0}>
            <Typography variant="h5" component="h1" align="center" sx={{ mb: 1 }}>
              Verify your email
            </Typography>
            <Stack spacing={1.25} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  fontWeight: 700,
                }}
              >
                ✓
              </Box>
              <Typography variant="h6" color="text.primary">
                Account created successfully!
              </Typography>
              <Typography variant="body2">We&apos;ve sent a verification link to:</Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{ bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, wordBreak: 'break-all' }}
              >
                <strong>{verificationEmail}</strong>
              </Typography>
              <Typography variant="body2">
                Please check your email and click the verification link to complete your registration.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The link will expire in 24 hours.
              </Typography>
              <Button variant="contained" fullWidth onClick={() => navigate('/signin')}>
                Back to Sign In
              </Button>
              <Button variant="outlined" fullWidth onClick={() => setSignupSuccess(false)}>
                Use different email
              </Button>
            </Stack>
          </Paper>
        </Fade>
      </Box>
    );
  }

  return (
    <Box className="auth-page-container ch-page-enter">
      <Box className="auth-page-main">
        <Fade in timeout={220}>
          <Box className="auth-page-stack ch-card-enter">
            <Link
              component={RouterLink}
              to="/"
              underline="none"
              color="primary"
              className="auth-page-back-link"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                alignSelf: 'flex-start',
                mb: 1.25,
              }}
            >
              <ArrowBackIcon fontSize="small" />
              Back to Home
            </Link>

            <Paper className="auth-page-box" elevation={0}>
              <Typography variant="h5" component="h1" align="center" sx={{ mb: 2 }}>
                Sign up for CofounderHubs
              </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.25}>
                {signupError && <Alert severity="error">{signupError}</Alert>}
                {googleError && <Alert severity="error">{googleError}</Alert>}

                <TextField
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                />

                <TextField
                  label="Email address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />

                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  helperText="Make sure it's at least 8 characters."
                  FormHelperTextProps={{ sx: { m: 0, mt: 0.35 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm password"
                  name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirm ? 'Hide password' : 'Show password'}
                          onClick={() => setShowConfirm((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button type="submit" variant="contained" color="primary" fullWidth>
                  Create account
                </Button>

                <Divider>
                  <Typography variant="caption" color="text.secondary">
                    or
                  </Typography>
                </Divider>

                <GoogleSignInButton onError={setGoogleError} />

                <Typography variant="caption" color="text.secondary" align="center">
                  By creating an account, you agree to the{' '}
                  <Link href="/terms-of-service" underline="hover" color="primary">
                    Terms of Service
                  </Link>
                  .
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link component={RouterLink} to="/signin" underline="hover" color="primary" fontWeight={500}>
                  Sign in
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Posting a job?{' '}
                <Link component={RouterLink} to="/post-job" underline="hover" color="primary" fontWeight={500}>
                  Use company work email
                </Link>
              </Typography>
            </Box>
            </Paper>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default SignUp;
